/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { CollectionContentPolicy } from '../models';
import { ContentClassifierGet } from '../models';
import { ContentClassifierSave } from '../models';
import { ContentPolicyGetMany } from '../models';
import { ContentPolicyGetOne } from '../models';
import { ContentPolicySave } from '../models';
import { ContentPolicyValidationResult } from '../models';
import { ContentRuleGet } from '../models';
import { ContentRuleSave } from '../models';
/**
 * PolicyApi - axios parameter creator
 * @export
 */
export const PolicyApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdContentClassifiersGet: async (account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdContentClassifiersGet.');
            }
            const localVarPath = `/accounts/{account_id}/content-classifiers`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdContentClassifiersPost: async (account_id: string, body?: ContentClassifierSave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdContentClassifiersPost.');
            }
            const localVarPath = `/accounts/{account_id}/content-classifiers`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdContentPoliciesGet: async (account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdContentPoliciesGet.');
            }
            const localVarPath = `/accounts/{account_id}/content-policies`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdContentPoliciesPost: async (account_id: string, body?: ContentPolicySave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdContentPoliciesPost.');
            }
            const localVarPath = `/accounts/{account_id}/content-policies`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        classifierRulesRuleIdDelete: async (rule_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'rule_id' is not null or undefined
            if (rule_id === null || rule_id === undefined) {
                throw new RequiredError('rule_id','Required parameter rule_id was null or undefined when calling classifierRulesRuleIdDelete.');
            }
            const localVarPath = `/classifier-rules/{rule_id}`
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(rule_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        classifierRulesRuleIdGet: async (rule_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'rule_id' is not null or undefined
            if (rule_id === null || rule_id === undefined) {
                throw new RequiredError('rule_id','Required parameter rule_id was null or undefined when calling classifierRulesRuleIdGet.');
            }
            const localVarPath = `/classifier-rules/{rule_id}`
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(rule_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        classifierRulesRuleIdPut: async (rule_id: string, body?: ContentRuleSave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'rule_id' is not null or undefined
            if (rule_id === null || rule_id === undefined) {
                throw new RequiredError('rule_id','Required parameter rule_id was null or undefined when calling classifierRulesRuleIdPut.');
            }
            const localVarPath = `/classifier-rules/{rule_id}`
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(rule_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdContentPolicyGet: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdContentPolicyGet.');
            }
            const localVarPath = `/collections/{collection_id}/content-policy`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {CollectionContentPolicy} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdContentPolicyPut: async (collection_id: string, body?: CollectionContentPolicy, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdContentPolicyPut.');
            }
            const localVarPath = `/collections/{collection_id}/content-policy`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentClassifiersClassifierIdDelete: async (classifier_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'classifier_id' is not null or undefined
            if (classifier_id === null || classifier_id === undefined) {
                throw new RequiredError('classifier_id','Required parameter classifier_id was null or undefined when calling contentClassifiersClassifierIdDelete.');
            }
            const localVarPath = `/content-classifiers/{classifier_id}`
                .replace(`{${"classifier_id"}}`, encodeURIComponent(String(classifier_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentClassifiersClassifierIdGet: async (classifier_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'classifier_id' is not null or undefined
            if (classifier_id === null || classifier_id === undefined) {
                throw new RequiredError('classifier_id','Required parameter classifier_id was null or undefined when calling contentClassifiersClassifierIdGet.');
            }
            const localVarPath = `/content-classifiers/{classifier_id}`
                .replace(`{${"classifier_id"}}`, encodeURIComponent(String(classifier_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentClassifiersClassifierIdPut: async (classifier_id: string, body?: ContentClassifierSave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'classifier_id' is not null or undefined
            if (classifier_id === null || classifier_id === undefined) {
                throw new RequiredError('classifier_id','Required parameter classifier_id was null or undefined when calling contentClassifiersClassifierIdPut.');
            }
            const localVarPath = `/content-classifiers/{classifier_id}`
                .replace(`{${"classifier_id"}}`, encodeURIComponent(String(classifier_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentClassifiersClassifierIdRulesGet: async (classifier_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'classifier_id' is not null or undefined
            if (classifier_id === null || classifier_id === undefined) {
                throw new RequiredError('classifier_id','Required parameter classifier_id was null or undefined when calling contentClassifiersClassifierIdRulesGet.');
            }
            const localVarPath = `/content-classifiers/{classifier_id}/rules`
                .replace(`{${"classifier_id"}}`, encodeURIComponent(String(classifier_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentClassifiersClassifierIdRulesPost: async (classifier_id: string, body?: ContentRuleSave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'classifier_id' is not null or undefined
            if (classifier_id === null || classifier_id === undefined) {
                throw new RequiredError('classifier_id','Required parameter classifier_id was null or undefined when calling contentClassifiersClassifierIdRulesPost.');
            }
            const localVarPath = `/content-classifiers/{classifier_id}/rules`
                .replace(`{${"classifier_id"}}`, encodeURIComponent(String(classifier_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentPoliciesPolicyIdDelete: async (policy_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'policy_id' is not null or undefined
            if (policy_id === null || policy_id === undefined) {
                throw new RequiredError('policy_id','Required parameter policy_id was null or undefined when calling contentPoliciesPolicyIdDelete.');
            }
            const localVarPath = `/content-policies/{policy_id}`
                .replace(`{${"policy_id"}}`, encodeURIComponent(String(policy_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentPoliciesPolicyIdGet: async (policy_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'policy_id' is not null or undefined
            if (policy_id === null || policy_id === undefined) {
                throw new RequiredError('policy_id','Required parameter policy_id was null or undefined when calling contentPoliciesPolicyIdGet.');
            }
            const localVarPath = `/content-policies/{policy_id}`
                .replace(`{${"policy_id"}}`, encodeURIComponent(String(policy_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentPoliciesPolicyIdPut: async (policy_id: string, body?: ContentPolicySave, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'policy_id' is not null or undefined
            if (policy_id === null || policy_id === undefined) {
                throw new RequiredError('policy_id','Required parameter policy_id was null or undefined when calling contentPoliciesPolicyIdPut.');
            }
            const localVarPath = `/content-policies/{policy_id}`
                .replace(`{${"policy_id"}}`, encodeURIComponent(String(policy_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {string} [page_id] 
         * @param {string} [content_item_id] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        contentPoliciesPolicyIdValidatePageGet: async (policy_id: string, page_id?: string, content_item_id?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'policy_id' is not null or undefined
            if (policy_id === null || policy_id === undefined) {
                throw new RequiredError('policy_id','Required parameter policy_id was null or undefined when calling contentPoliciesPolicyIdValidatePageGet.');
            }
            const localVarPath = `/content-policies/{policy_id}/validate-page`
                .replace(`{${"policy_id"}}`, encodeURIComponent(String(policy_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page_id !== undefined) {
                localVarQueryParameter['page_id'] = page_id;
            }

            if (content_item_id !== undefined) {
                localVarQueryParameter['content_item_id'] = content_item_id;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PolicyApi - functional programming interface
 * @export
 */
export const PolicyApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentClassifiersGet(account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<ContentClassifierGet>>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).accountsAccountIdContentClassifiersGet(account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentClassifiersPost(account_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).accountsAccountIdContentClassifiersPost(account_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentPoliciesGet(account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<ContentPolicyGetMany>>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).accountsAccountIdContentPoliciesGet(account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentPoliciesPost(account_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).accountsAccountIdContentPoliciesPost(account_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdDelete(rule_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).classifierRulesRuleIdDelete(rule_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdGet(rule_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<ContentRuleGet>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).classifierRulesRuleIdGet(rule_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdPut(rule_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).classifierRulesRuleIdPut(rule_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentPolicyGet(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<CollectionContentPolicy>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).collectionsCollectionIdContentPolicyGet(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {CollectionContentPolicy} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentPolicyPut(collection_id: string, body?: CollectionContentPolicy, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).collectionsCollectionIdContentPolicyPut(collection_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdDelete(classifier_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentClassifiersClassifierIdDelete(classifier_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdGet(classifier_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<ContentClassifierGet>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentClassifiersClassifierIdGet(classifier_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdPut(classifier_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentClassifiersClassifierIdPut(classifier_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdRulesGet(classifier_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<ContentRuleGet>>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentClassifiersClassifierIdRulesGet(classifier_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdRulesPost(classifier_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentClassifiersClassifierIdRulesPost(classifier_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdDelete(policy_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentPoliciesPolicyIdDelete(policy_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdGet(policy_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<ContentPolicyGetOne>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentPoliciesPolicyIdGet(policy_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdPut(policy_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentPoliciesPolicyIdPut(policy_id, body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {string} [page_id] 
         * @param {string} [content_item_id] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdValidatePageGet(policy_id: string, page_id?: string, content_item_id?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<ContentPolicyValidationResult>>> {
            const localVarAxiosArgs = await PolicyApiAxiosParamCreator(configuration).contentPoliciesPolicyIdValidatePageGet(policy_id, page_id, content_item_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * PolicyApi - factory interface
 * @export
 */
export const PolicyApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentClassifiersGet(account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<ContentClassifierGet>>> {
            return PolicyApiFp(configuration).accountsAccountIdContentClassifiersGet(account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentClassifiersPost(account_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).accountsAccountIdContentClassifiersPost(account_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} account_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentPoliciesGet(account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<ContentPolicyGetMany>>> {
            return PolicyApiFp(configuration).accountsAccountIdContentPoliciesGet(account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} account_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdContentPoliciesPost(account_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).accountsAccountIdContentPoliciesPost(account_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdDelete(rule_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).classifierRulesRuleIdDelete(rule_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdGet(rule_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<ContentRuleGet>> {
            return PolicyApiFp(configuration).classifierRulesRuleIdGet(rule_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} rule_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async classifierRulesRuleIdPut(rule_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).classifierRulesRuleIdPut(rule_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentPolicyGet(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<CollectionContentPolicy>> {
            return PolicyApiFp(configuration).collectionsCollectionIdContentPolicyGet(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} collection_id 
         * @param {CollectionContentPolicy} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdContentPolicyPut(collection_id: string, body?: CollectionContentPolicy, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).collectionsCollectionIdContentPolicyPut(collection_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdDelete(classifier_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).contentClassifiersClassifierIdDelete(classifier_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdGet(classifier_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<ContentClassifierGet>> {
            return PolicyApiFp(configuration).contentClassifiersClassifierIdGet(classifier_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentClassifierSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdPut(classifier_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).contentClassifiersClassifierIdPut(classifier_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdRulesGet(classifier_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<ContentRuleGet>>> {
            return PolicyApiFp(configuration).contentClassifiersClassifierIdRulesGet(classifier_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} classifier_id 
         * @param {ContentRuleSave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentClassifiersClassifierIdRulesPost(classifier_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).contentClassifiersClassifierIdRulesPost(classifier_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdDelete(policy_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).contentPoliciesPolicyIdDelete(policy_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdGet(policy_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<ContentPolicyGetOne>> {
            return PolicyApiFp(configuration).contentPoliciesPolicyIdGet(policy_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {ContentPolicySave} [body] Body is copied verbatim
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdPut(policy_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return PolicyApiFp(configuration).contentPoliciesPolicyIdPut(policy_id, body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} policy_id 
         * @param {string} [page_id] 
         * @param {string} [content_item_id] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async contentPoliciesPolicyIdValidatePageGet(policy_id: string, page_id?: string, content_item_id?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<ContentPolicyValidationResult>> {
            return PolicyApiFp(configuration).contentPoliciesPolicyIdValidatePageGet(policy_id, page_id, content_item_id, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PolicyApi - object-oriented interface
 * @export
 * @class PolicyApi
 * @extends {BaseAPI}
 */
export class PolicyApi extends BaseAPI {
    /**
     * 
     * @param {string} account_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async accountsAccountIdContentClassifiersGet(account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<ContentClassifierGet>>> {
        return PolicyApiFp(this.configuration).accountsAccountIdContentClassifiersGet(account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} account_id 
     * @param {ContentClassifierSave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async accountsAccountIdContentClassifiersPost(account_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).accountsAccountIdContentClassifiersPost(account_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} account_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async accountsAccountIdContentPoliciesGet(account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<ContentPolicyGetMany>>> {
        return PolicyApiFp(this.configuration).accountsAccountIdContentPoliciesGet(account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} account_id 
     * @param {ContentPolicySave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async accountsAccountIdContentPoliciesPost(account_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).accountsAccountIdContentPoliciesPost(account_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} rule_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async classifierRulesRuleIdDelete(rule_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).classifierRulesRuleIdDelete(rule_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} rule_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async classifierRulesRuleIdGet(rule_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<ContentRuleGet>> {
        return PolicyApiFp(this.configuration).classifierRulesRuleIdGet(rule_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} rule_id 
     * @param {ContentRuleSave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async classifierRulesRuleIdPut(rule_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).classifierRulesRuleIdPut(rule_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} collection_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async collectionsCollectionIdContentPolicyGet(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<CollectionContentPolicy>> {
        return PolicyApiFp(this.configuration).collectionsCollectionIdContentPolicyGet(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} collection_id 
     * @param {CollectionContentPolicy} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async collectionsCollectionIdContentPolicyPut(collection_id: string, body?: CollectionContentPolicy, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).collectionsCollectionIdContentPolicyPut(collection_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} classifier_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentClassifiersClassifierIdDelete(classifier_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).contentClassifiersClassifierIdDelete(classifier_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} classifier_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentClassifiersClassifierIdGet(classifier_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<ContentClassifierGet>> {
        return PolicyApiFp(this.configuration).contentClassifiersClassifierIdGet(classifier_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} classifier_id 
     * @param {ContentClassifierSave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentClassifiersClassifierIdPut(classifier_id: string, body?: ContentClassifierSave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).contentClassifiersClassifierIdPut(classifier_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} classifier_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentClassifiersClassifierIdRulesGet(classifier_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<ContentRuleGet>>> {
        return PolicyApiFp(this.configuration).contentClassifiersClassifierIdRulesGet(classifier_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} classifier_id 
     * @param {ContentRuleSave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentClassifiersClassifierIdRulesPost(classifier_id: string, body?: ContentRuleSave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).contentClassifiersClassifierIdRulesPost(classifier_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} policy_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentPoliciesPolicyIdDelete(policy_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).contentPoliciesPolicyIdDelete(policy_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} policy_id 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentPoliciesPolicyIdGet(policy_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<ContentPolicyGetOne>> {
        return PolicyApiFp(this.configuration).contentPoliciesPolicyIdGet(policy_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} policy_id 
     * @param {ContentPolicySave} [body] Body is copied verbatim
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentPoliciesPolicyIdPut(policy_id: string, body?: ContentPolicySave, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return PolicyApiFp(this.configuration).contentPoliciesPolicyIdPut(policy_id, body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @param {string} policy_id 
     * @param {string} [page_id] 
     * @param {string} [content_item_id] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PolicyApi
     */
    public async contentPoliciesPolicyIdValidatePageGet(policy_id: string, page_id?: string, content_item_id?: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<ContentPolicyValidationResult>> {
        return PolicyApiFp(this.configuration).contentPoliciesPolicyIdValidatePageGet(policy_id, page_id, content_item_id, options).then((request) => request(this.axios, this.basePath));
    }
}
